import { booleanAttribute } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { eventReviewProps } from "./event-review";

export const props = Object.freeze({
  /**
   * The title of the modal
   *
   * @type {String}
   * @default "Checkout"
   */
  modalTitle: {
    type: String,
    default: "Checkout",
  },

  /**
   * The text for the activator button
   *
   * @type {String}
   * @default "Get Started!"
   */
  activatorText: {
    type: String,
    default: "Get Started!",
  },
  /**
   * The text for the CTA activator button
   *
   * @type {String}
   * @default undefined
   */
  mobileCtaActivatorText: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` on the activator button. Defaults to the events
   * catalog value because the `type` validator pins sp-checkout to event
   * registration.
   *
   * @type {String}
   * @default TestId.events.register
   */
  activatorTestId: {
    type: String,
    default: TestId.events.register,
  },
  /**
   * Whether the form has a payment step
   *
   * @type {Boolean|String}
   * @default false
   */
  payment: booleanAttribute(),

  /**
   * Whether the form has a review step
   *
   * @type {Boolean|String}
   * @default false
   */
  review: booleanAttribute(),

  /**
   * The type of checkout
   *
   * @type {String}
   * @values ["event"]
   * @default undefined
   */
  type: {
    type: String,
    default: undefined,
    validator: (value) => ["event"].includes(value),
  },

  /**
   * The resource being checked out, as an object or a JSON string. `type="event"` is the only
   * checkout supported today, and it requires this.
   *
   * @type {String|Object}
   * @default undefined
   */
  resource: {
    type: [String, Object],
    default: undefined,
  },

  /**
   * The URL to add the resource to the cart
   *
   * @type {String}
   * @default undefined
   */
  addToCartUrl: {
    type: String,
    default: undefined,
  },
  /**
   * Whether the modal should be activated on mount
   * This is useful for when the modal should be opened automatically, and also run all logic bound to the activator
   *
   * @type {Boolean|String}
   * @default false
   */
  open: booleanAttribute(),
  /**
   * Whether the user has RSVP'd to the event
   *
   * @type {Boolean|String}
   * @default false
   */
  rsvp: booleanAttribute(),
  /**
   * The URL to decline the RSVP
   *
   * @type {String}
   * @default undefined
   */
  rsvpDeclineUrl: {
    type: String,
    default: undefined,
  },

  /**
   * Whether the parent failed to load the resource (e.g. the event fetch failed).
   * When true, the loading overlay shows a retry instead of spinning forever.
   *
   * @type {Boolean|String}
   * @default false
   */
  resourceError: booleanAttribute(),

  /**
   * Message shown in the loading overlay when the initial load fails.
   *
   * @type {String}
   * @default undefined
   */
  loadErrorMessage: {
    type: String,
    default: undefined,
  },

  /**
   * Label for the retry button shown when the initial load fails.
   *
   * @type {String}
   * @default undefined
   */
  retryActivatorText: {
    type: String,
    default: undefined,
  },

  ...eventReviewProps,
});
